import * as Sentry from "@sentry/react";
import { AppContext } from '@lightspeed-invoicing/core-client/frontend';
import React, { Component } from 'react';
import { IntlProvider, addLocaleData } from 'react-intl';
import EventBus from './common/EventBus.js';
import GlobalHelper from "./common/GlobalHelper.jsx";
import ErrorBoundary from "./common/error.component.jsx";
import LoadingSpinner from './common/spinner/loading.component.jsx';
import { Settings } from "./settings/account/Settings.jsx";
import ShopifyAppContext from './shopify/shopifyappcontext.js';
const queryString = require('query-string')
class AppContainer extends Component {
    constructor(props, context) {
        super(props, context)

        this.state = {
            isPublic: this.props.isPublic || false,
            isLoading: true,
            user: null,
            messages: null,
            locale: null,
        }
        this.handleEvents = this.handleEvents.bind(this)
        this.initReferralProgram = this.initReferralProgram.bind(this)
        this.setSentryContext = this.setSentryContext.bind(this)
        this.initSentry = this.initSentry.bind(this)
        EventBus.listen(EventBus.TopicLocaleChange, 'app-container-component', this.handleEvents)
    }

    getLocaleData() {
        let localeList = []
        for (const [_, lang] of GlobalHelper.getSupportedLanguages().entries()) {
            localeList.push(...require('react-intl/locale-data/' + lang))
        }
        return localeList
    }


    handleEvents(event) {
        AppContext.setLocale(this.getSupportedLanguage(event.getData().locale))
        const locale = AppContext.getLocale()
        const locales = require('../locales/' + locale.getLanguage() + '.json')
        this.setState({
            messages: AppContext.getLocale().loadTranslation(locales),
            locale: AppContext.getLocale().getLanguage(),
        })
    }

    loadApp() {
        const uri = this.props.uri

        if (uri.shop && (window.self != window.top)) {
            AppContext.startPublicSession().then(_ => {
                ShopifyAppContext.setOrigin(uri.shop, uri.host).then(_ => {
                    setTimeout(() => {
                        ShopifyAppContext.getSessionTokenPromise().then(shopifySessionToken => {
                            AppContext.getApiClient().get('/auth/' + ShopifyAppContext.integrationKey + '/token?' + queryString.stringify(uri) +
                                `&sessionToken=${shopifySessionToken}`).then(res => {
                                    if (!res.error) {
                                        AppContext.startWithAuthTokenResponse(res).then(_ => {
                                            this.initStateAfterAppContextStart()
                                        })
                                    } else {
                                        fetch('/session', {
                                            credentials: "same-origin"
                                        }).then(res => {
                                            if (res.status == 200) {
                                                this.startPrivateSession()
                                            } else {
                                                this.startPublicSession()
                                            }
                                        })
                                    }
                                })
                        })
                    }, 1000)
                })
            })
        } else {
            if (this.state.isPublic) {
                this.startPublicSession()
            } else {
                this.startPrivateSession()
            }
        }

        this.initReferralProgram()
    }

    startPublicSession() {
        AppContext.setLocale(this.getSupportedLanguage(this.props.locale))
        AppContext.startPublicSession().then(_ => {
            const locale = AppContext.getLocale()
            const locales = require('../locales/' + locale.getLanguage() + '.json')
            this.setState({
                isLoading: false,
                messages: AppContext.getLocale().loadTranslation(locales),
                locale: AppContext.getLocale().getLanguage(),
            }, () => this.initSentry())
        })
    }

    startPrivateSession() {
        AppContext.start().then(_ => {
            if (AppContext.isAuthenticated()) {
                this.initStateAfterAppContextStart()
                const isOnBoarding = AppContext.getAccount().attributes.onBoardingStatus === "pending"
                if (isOnBoarding && GlobalHelper.getShopifyIntegrationOrigin()) {
                    ShopifyAppContext.setOrigin(GlobalHelper.getShopifyIntegrationOrigin()).then(() => { })
                }
            }
        })
    }

    async initStateAfterAppContextStart() {
        await Settings.load()
        let languageFile = this.getSupportedLanguage(AppContext.getLocale().getLanguage())
        this.setState({
            isLoading: false,
            user: AppContext.getUserLoggedIn(),
            messages: AppContext.getLocale().loadTranslation(require(`../locales/${languageFile}.json`)),
            locale: AppContext.getLocale().getLanguage(),
        }, () => this.initSentry())
    }

    initSentry() {
        if (AppContext.getOptions() && AppContext.getOptions().environment !== "dev") {
            Sentry.init({
                dsn: "https://7bdac0f4e28a4a32a69fb10eaf90bda6@o120014.ingest.sentry.io/264455",
                releases: process.env.APP_VERSION,
                environment: AppContext.getOptions().environment ? AppContext.getOptions().environment : process.env.NODE_ENV,
                ignoreErrors: ['Object Not Found Matching'],
            });
        }
        this.setSentryContext()
    }

    setSentryContext() {
        if (AppContext.getOptions() && AppContext.getOptions().environment !== "dev" && AppContext.session && AppContext.session.isAuthenticated()) {
            let userLogged = AppContext.getUserLoggedIn()
            Sentry.setContext("QM User Information", {
                email: userLogged.attributes.email,
                userId: userLogged.id,
                accountId: userLogged.attributes.accountId
            });
        }
    }

    initReferralProgram() {
        if (!window.growsurf) {
            setTimeout(this.initReferralProgram, 200)
            return
        }
        if (!growsurf.getParticipantId() && AppContext.isAuthenticated()) {
            growsurf.addParticipant({
                email: AppContext.getUserLoggedIn().attributes.email,
                firstName: AppContext.getUserLoggedIn().attributes.firstName,
                lastName: AppContext.getUserLoggedIn().attributes.lastName,
                company: AppContext.getAccount().attributes.companyName,
            }, (_) => {
                this.openGrowSurfIfNeeded()
            })
        } else {
            this.openGrowSurfIfNeeded()
        }

    }

    openGrowSurfIfNeeded() {
        const uri = queryString.parse(location.search)
        if (uri.rp == '1' && growsurf) {
            growsurf.open()
        }
    }

    getSupportedLanguage(locale) {
        return GlobalHelper.formatSupportedLanguage(locale)
    }

    componentDidMount() {
        this.checkIfPdf()
        this.setState({
            isLoading: true,
        }, this.loadApp)
    }

    checkIfPdf() {
        const url = window.location.pathname.toLowerCase();
        if (url.includes('/pdf')) {
            document.body.style.background = '#FFFFFF'
        }
    }

    render() {
        if (this.state.isLoading) {
            return <LoadingSpinner marginTop="20px" />
        }
        addLocaleData(this.getLocaleData())
        return (
            <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
                <IntlProvider
                    locale={this.state.locale}
                    messages={this.state.messages}>
                    {this.props.children}
                </IntlProvider>
            </Sentry.ErrorBoundary>
        );
    }
}

export default Sentry.withProfiler(AppContainer)


// WEBPACK FOOTER //
// ./src/appcontainer.component.jsx